import { useReducer, useMemo, useCallback } from 'react'
import { CustomExchangeRate, StatementType } from '../types/exchangeRate'
import { CustomReportDataType } from '../../../redux/context/customReports/typesVariable'

export type ModifiedCustomExchangeRate = Omit<CustomExchangeRate, 'value'> & {
  value?: string | number | null
}

type ExchangeRateAction =
  | { type: 'UPDATE_RATE'; payload: ModifiedCustomExchangeRate }
  | {
      type: 'DELETE_RATE'
      payload: { statementType: StatementType; dataType: CustomReportDataType; month: number; year: number }
    }
  | { type: 'RESET' }

interface ExchangeRateState {
  modifiedRates: Map<string, ModifiedCustomExchangeRate>
}

const areValuesEqual = (
  value1: string | number | null | undefined,
  value2: string | number | null | undefined
): boolean => {
  // Jos molemmat null/undefined
  if (value1 == null && value2 == null) return true

  // Jos toinen null/undefined
  if (value1 == null || value2 == null) return false

  // Konvertoi molemmat numeroiksi vertailua varten
  const num1 = parseFloat(String(value1))
  const num2 = parseFloat(String(value2))

  // Jos molemmat ovat NaN, ne ovat "samat"
  if (Number.isNaN(num1) && Number.isNaN(num2)) return true

  // Muuten vertaa numeroina
  return num1 === num2
}
const getKey = (
  rate:
    | ModifiedCustomExchangeRate
    | { statementType: StatementType; dataType: CustomReportDataType; month: number; year: number }
) => `${rate.statementType}-${rate.dataType}-${rate.month}-${rate.year}`

const exchangeRateReducer = (state: ExchangeRateState, action: ExchangeRateAction): ExchangeRateState => {
  switch (action.type) {
    case 'UPDATE_RATE': {
      const newModified = new Map(state.modifiedRates)
      newModified.set(getKey(action.payload), action.payload)
      return { modifiedRates: newModified }
    }

    case 'DELETE_RATE': {
      const newModified = new Map(state.modifiedRates)
      newModified.delete(getKey(action.payload))
      return { modifiedRates: newModified }
    }

    case 'RESET':
      return { modifiedRates: new Map() }

    default:
      return state
  }
}

export const useExchangeRateState = (originalRates: CustomExchangeRate[] = []) => {
  const [state, dispatch] = useReducer(exchangeRateReducer, {
    modifiedRates: new Map()
  })

  // Yhdistetty data
  const allRates = useMemo(() => {
    if (state.modifiedRates.size === 0) return originalRates

    const modifiedKeys = new Set(state.modifiedRates.keys())

    const filteredOriginal = originalRates.filter(rate => !modifiedKeys.has(getKey(rate)))

    const modifiedArray = Array.from(state.modifiedRates.values())

    return [...filteredOriginal, ...modifiedArray] as CustomExchangeRate[]
  }, [originalRates, state.modifiedRates])

  // Luo Map originalRates avaimista tehokkuuden vuoksi
  const originalRateMap = useMemo(() => {
    const map = new Map<string, CustomExchangeRate>()
    originalRates.forEach(rate => {
      map.set(getKey(rate), rate)
    })
    return map
  }, [originalRates])

  // Filter modifications that should be counted
  const getCountableModifications = useMemo(() => {
    return Array.from(state.modifiedRates.values()).filter(modifiedRate => {
      const key = getKey(modifiedRate)
      const originalRate = originalRateMap.get(key)

      // If value is null and doesn't exist in originalRates, don't count
      if (modifiedRate.value === null && !originalRate) {
        return false
      }

      // If original rate exists and values are equal, don't count
      if (originalRate && areValuesEqual(modifiedRate.value, originalRate.value)) {
        return false
      }

      return true
    })
  }, [state.modifiedRates, originalRateMap])

  const hasModifications = useMemo(() => {
    return getCountableModifications.length > 0
  }, [getCountableModifications])

  const modificationCount = useMemo(() => {
    return getCountableModifications.length
  }, [getCountableModifications])

  // API funktiot
  const updateRate = useCallback((rate: ModifiedCustomExchangeRate) => {
    dispatch({ type: 'UPDATE_RATE', payload: rate })
  }, [])

  const deleteRate = useCallback(
    (statementType: StatementType, dataType: CustomReportDataType, month: number, year: number) => {
      dispatch({ type: 'DELETE_RATE', payload: { statementType, dataType, month, year } })
    },
    []
  )

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return {
    allRates,
    hasModifications,
    modificationCount,
    updateRate, // Yksittäinen rate
    deleteRate, // Poista rate
    reset // Nollaa kaikki
  }
}
